import { FC, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Modal } from "capitalrx-components-2.0";
import { email as validateEmail, phone as validatePhone } from "capitalrx-utilities/lib/form-validation";
import { Checkbox, Dropdown, FileEntry, FileUpload, Form, SubmitButton, TextArea, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Button, Spinner, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Regular, Semibold } from "capitalrx-components-2.0/lib/typography";

import { Auth, createFeedback, Feedback, urlForUploadFeedback } from "@/api";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { getThemeButton } from "@/utils";
import { useSuccessHandler } from "@/hooks";

import styles from "./FeedbackButton.module.scss";

interface Props {
  isModalOpen: boolean;
  handleModal: () => void;
}

type UploadScreenshotResult = {
  name: string;
  type: string;
  bucket: string;
  key: string;
  post: {
    url: string;
    fields: Record<string, string>;
  };
};

async function uploadScreenshots(
  screenshots: FileEntry[],
): Promise<UploadScreenshotResult[]> {
  if (!screenshots?.length) return [];

  const results: UploadScreenshotResult[] = [];

  for (const screenshot of screenshots) {
    const { file, name, type } = screenshot;

    const uploadResponse = await urlForUploadFeedback({
      file_name: name,
    });

    const formData = new FormData();
    Object.entries(uploadResponse.post.fields).forEach(([key, value]) => {
      formData.append(key, value as string);
    });
    formData.append("file", file);

    const response = await fetch(uploadResponse.post.url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload screenshot: ${name}`);
    }

    results.push({
      name: name,
      type: type ?? "application/octet-stream",
      bucket: uploadResponse.bucket,
      key: uploadResponse.key,
      post: uploadResponse.post,
    });
  }

  return results;
}

export const FeedbackModal: FC<Props> = ({ isModalOpen, handleModal }) => {
  const form = useForm();
  const { isUserLoggedIn, getUser } = useContext(AuthenticationContext);
  const { primary_color, primary_hover_color, secondary_hover_color } = useContext(ThemeSelectorContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const successHandler = useSuccessHandler();
  const showAlert = useAlert();

  const [user, setUser] = useState<Auth.GetUserResponse>();

  const checkUser = async () =>{
    setUser(await getUser());
  };

  useEffect(() => {
    checkUser();
  }, [isUserLoggedIn, getUser]);

  const handleFormSubmit = async (): Promise<void> => {
    try {
      setIsSubmitting(true);
      const values = form.getValues() as Feedback.CreateFeedbackRequestSchema;
      if (isUserLoggedIn()) {
        values.email = user?.email;
      }
      if (values.is_opt_in_notify === null) {
        values.is_opt_in_notify = false;
      }

      const uploadedScreenshots = await uploadScreenshots(
        (values.screenshots) ?? [],
      );

      const payload: Feedback.CreateFeedbackRequestSchema = {
        feedback_type: values.feedback_type,
        description: values.description,
        screenshots: uploadedScreenshots,
        email: values.email,
        phone: values?.phone,
        is_opt_in_notify: values?.is_opt_in_notify,
      };

      await createFeedback(payload);
      successHandler({ message: "Successfully submitted feedback" });
      form.reset();
      handleModal();
    } catch (error) {
      console.error(error);
      showAlert({
        type: "error",
        style: "banner",
        message: "Error submitting feedback. Reload page and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal visible={isModalOpen} onClose={handleModal} title={<Semibold className={styles.modalTitle}>Share Feedback</Semibold>}>
      <Form form={form} onSubmit={handleFormSubmit}>
        <Stack spacing="medium-large">
          {isSubmitting && <Shelf><Spinner size="large"/></Shelf>}
          {!isSubmitting && (
            <Stack>
              <Body>Share any issues, ideas, or comments to help us improve</Body>
              <Dropdown.Field className={styles.componentText} label="Feedback Type" name="feedback_type" required>
                <Dropdown.Option value="issue"><Regular>Report an issue</Regular></Dropdown.Option>
                <Dropdown.Option value="enhancement"><Regular>Suggest a feature or idea</Regular></Dropdown.Option>
                <Dropdown.Option value="other"><Regular>Other</Regular></Dropdown.Option>
              </Dropdown.Field>
              <TextArea.Field name="description" label="Describe Your Feedback" lines={8} required />
              <FileUpload.Field className={styles.componentText} name="screenshots" label="Add Screenshots" allowMultiple />
              {!isUserLoggedIn() && (
                <>
                  <Textbox.Field name="email" label="Email" validator={validateEmail} instructions="Provide your email so we can contact you with followU+002dup or updates about your feedback" />
                  <Textbox.Field name="phone" label="Phone" validator={validatePhone} instructions="Provide your phone number if you prefer to receive updates by phone" />
                </>
              )}
              {isUserLoggedIn() && (
                <Checkbox.Field name="is_opt_in_notify" hideLabel label="You may contact me for follow-up or updates" />
              )}
              <Shelf spacing="small">
                <Shelf.Spacer />
                <Button appearance={getThemeButton("secondary", primary_color, secondary_hover_color)} onClick={handleModal}>Cancel</Button>
                <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)}>Submit</SubmitButton>
              </Shelf>
            </Stack>
         )}
        </Stack>
      </Form>
    </Modal>
  );
};
